import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Check, 
  ChevronDown, 
  ChevronUp, 
  Edit3, 
  Sparkles,
  ChefHat,
  Tag,
  Camera,
  Wand2,
  Upload,
  X,
  Loader2,
  ImageIcon,
  AlertTriangle
} from 'lucide-react';
import { RecipeFormData } from '@/types/recipe';
import { generateRecipeImage } from '@/services/recipeService';

interface ParsedRecipePreviewProps {
  recipe: Partial<RecipeFormData>;
  onSave: (updatedRecipe?: Partial<RecipeFormData>) => void;
  onEdit: () => void;
  onBack: () => void;
}

const ParsedRecipePreview: React.FC<ParsedRecipePreviewProps> = ({
  recipe,
  onSave,
  onEdit,
  onBack,
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    ingredients: false,
    method: false,
    notes: false,
  });
  const [recipeImage, setRecipeImage] = useState<string | null>(recipe.image_url || null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Count items in a markdown list
  const countItems = (text: string | undefined): number => {
    if (!text) return 0;
    const lines = text.split('\n').filter((line) => line.trim().match(/^[-*•\d.]/));
    return lines.length;
  };

  // Get preview of a section (first few lines)
  const getPreview = (text: string | undefined, maxLines = 3): string[] => {
    if (!text) return [];
    const lines = text.split('\n').filter((line) => line.trim());
    return lines.slice(0, maxLines);
  };

  const ingredientCount = countItems(recipe.ingredients);
  const methodSteps = countItems(recipe.method);

  // Build a list of parse-quality warnings shown to the user before they save.
  // Non-blocking — they can still save and edit later. The aim is to surface cases
  // where the AI truncated output, missed a section, or produced suspiciously thin
  // content (e.g. 1 ingredient for a 25-ingredient recipe).
  const parseWarnings: string[] = [];
  if (!recipe.title || recipe.title.trim().length === 0) {
    parseWarnings.push('No title detected. Please add one before saving.');
  }
  if (!recipe.ingredients || recipe.ingredients.trim().length === 0) {
    parseWarnings.push('No ingredients found. The source may not have an ingredients section, or the AI failed to extract it.');
  } else if (ingredientCount < 3) {
    parseWarnings.push(
      `Only ${ingredientCount} ingredient${ingredientCount === 1 ? '' : 's'} found. This may be incomplete — review the source or click "Edit Details" to add more.`
    );
  }
  if (!recipe.method || recipe.method.trim().length === 0) {
    parseWarnings.push('No method/instructions found. The source may not have instructions, or the AI failed to extract them.');
  } else if (methodSteps < 2) {
    parseWarnings.push(
      `Only ${methodSteps} method step${methodSteps === 1 ? '' : 's'} found. This may be incomplete — review the source or click "Edit Details" to add more.`
    );
  }
  if (!recipe.description || recipe.description.trim().length === 0) {
    parseWarnings.push('No description detected. The AI should have written one — consider adding a short description manually.');
  }

  // Handle AI image generation
  const handleGenerateImage = async () => {
    if (!recipe.title) return;
    
    setIsGeneratingImage(true);
    setImageError(null);
    
    try {
      const result = await generateRecipeImage(recipe.title, recipe.description || '');
      if (result.success && result.imageUrl) {
        setRecipeImage(result.imageUrl);
      } else {
        setImageError(result.error || 'Failed to generate image');
      }
    } catch {
      setImageError('Failed to generate image. Please try again.');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Handle file upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Reset input
    e.target.value = '';
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setImageError('Please select an image file (JPG, PNG, etc.)');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setImageError('Image must be less than 5MB');
      return;
    }
    
    // Read and set image
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      setRecipeImage(dataUrl);
      setImageError(null);
    };
    reader.onerror = () => {
      setImageError('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  // Handle save with image
  const handleSaveWithImage = () => {
    const updatedRecipe = recipeImage ? { ...recipe, image_url: recipeImage } : recipe;
    onSave(updatedRecipe);
  };

  // Remove image
  const handleRemoveImage = () => {
    setRecipeImage(null);
    setImageError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-gray-950 dark:via-emerald-950/20 dark:to-gray-950">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={onBack}
            className="mb-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>

        {/* Success Banner */}
        <div className="mb-6 flex items-center gap-3 p-4 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl border border-emerald-200 dark:border-emerald-800">
          <div className="p-2 rounded-full bg-emerald-500 text-white">
            <Check className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold text-emerald-800 dark:text-emerald-200">
              Recipe Parsed Successfully!
            </p>
            <p className="text-sm text-emerald-600 dark:text-emerald-400">
              Review the details below, then save or edit as needed
            </p>
          </div>
        </div>

        {/* Parse-Quality Warnings (non-blocking) */}
        {parseWarnings.length > 0 && (
          <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded-full bg-amber-500 text-white shrink-0 mt-0.5">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-amber-800 dark:text-amber-200 mb-1">
                  Parse looks thin — please review
                </p>
                <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1 list-disc list-inside">
                  {parseWarnings.map((warning, i) => (
                    <li key={i}>{warning}</li>
                  ))}
                </ul>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  You can still save the recipe — click "Edit Details" to add anything missing.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Image Section - Prominent at top */}
        <Card className="mb-6 border-0 shadow-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm ring-1 ring-amber-200/50 dark:ring-amber-700/30 overflow-hidden">
          <CardContent className="p-4 md:p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 text-white">
                <Camera className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Add a Photo
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Make your recipe stand out with a delicious image
                </p>
              </div>
            </div>

            {/* Image error message */}
            {imageError && (
              <div className="mb-4 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg flex items-center gap-2">
                <span className="text-red-500 text-sm">⚠️</span>
                <p className="text-xs text-red-600 dark:text-red-400 flex-1">{imageError}</p>
                <button onClick={() => setImageError(null)} className="text-red-400 hover:text-red-600">
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            {recipeImage ? (
              /* Image Preview */
              <div className="relative group">
                <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700">
                  <img 
                    src={recipeImage} 
                    alt={recipe.title || 'Recipe'} 
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Overlay actions */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-3">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-white/90 hover:bg-white text-gray-800"
                  >
                    <Upload className="h-3 w-3 mr-1" />
                    Replace
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleRemoveImage}
                    className="bg-white/90 hover:bg-white text-red-600"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Remove
                  </Button>
                </div>
                {/* Success indicator */}
                <div className="absolute top-2 right-2 px-2 py-1 bg-emerald-500 text-white text-xs rounded-full flex items-center gap-1">
                  <Check className="h-3 w-3" />
                  Ready
                </div>
              </div>
            ) : (
              /* No image - show options */
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-6 text-center">
                {isGeneratingImage ? (
                  <div className="py-4">
                    <Loader2 className="h-10 w-10 text-amber-500 mx-auto mb-3 animate-spin" />
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Creating your image...
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      AI is generating a beautiful photo for "{recipe.title}"
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-gray-400" />
                    </div>
                    <div className="flex flex-col sm:flex-row justify-center gap-2 mb-3">
                      <Button
                        onClick={handleGenerateImage}
                        className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md"
                      >
                        <Wand2 className="h-4 w-4 mr-2" />
                        Generate with AI
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="border-gray-300 dark:border-gray-600"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Photo
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      JPG, PNG up to 5MB • AI generation uses 1 ChefCoin
                    </p>
                  </>
                )}
              </div>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </CardContent>
        </Card>

        {/* Main Recipe Card */}
        <Card className="border-0 shadow-xl bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm ring-1 ring-gray-200/50 dark:ring-gray-700/50 overflow-hidden">
          {/* Recipe Header */}
          <CardHeader className="pb-4 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shrink-0">
                <ChefHat className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2 leading-tight">
                  {recipe.title || 'Untitled Recipe'}
                </h1>
                {recipe.description && (
                  <p className="text-gray-600 dark:text-gray-400 line-clamp-2">
                    {recipe.description}
                  </p>
                )}
              </div>
            </div>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {recipe.category && (
                <Badge variant="secondary" className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                  {recipe.category}
                </Badge>
              )}
              {recipe.tags && recipe.tags.length > 0 && (
                <>
                  {recipe.tags.slice(0, 3).map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      <Tag className="h-3 w-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
                  {recipe.tags.length > 3 && (
                    <Badge variant="outline" className="text-xs text-gray-500">
                      +{recipe.tags.length - 3} more
                    </Badge>
                  )}
                </>
              )}
            </div>
          </CardHeader>

          <CardContent className="p-6 space-y-4">
            {/* Ingredients Section */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('ingredients')}
                className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">🥘</span>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 dark:text-white">Ingredients</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {ingredientCount} items found
                    </p>
                  </div>
                </div>
                {expandedSections.ingredients ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </button>
              
              {!expandedSections.ingredients && recipe.ingredients && (
                <div className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
                  {getPreview(recipe.ingredients).map((line, i) => (
                    <div key={i} className="truncate">{line}</div>
                  ))}
                  {ingredientCount > 3 && (
                    <p className="text-gray-400 dark:text-gray-500 mt-1">
                      ... and {ingredientCount - 3} more
                    </p>
                  )}
                </div>
              )}
              
              {expandedSections.ingredients && recipe.ingredients && (
                <div className="p-4 text-sm text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
                  {recipe.ingredients}
                </div>
              )}
            </div>

            {/* Method Section */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('method')}
                className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">📋</span>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 dark:text-white">Method</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {methodSteps} steps
                    </p>
                  </div>
                </div>
                {expandedSections.method ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </button>
              
              {!expandedSections.method && recipe.method && (
                <div className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
                  {getPreview(recipe.method, 2).map((line, i) => (
                    <div key={i} className="truncate">{line}</div>
                  ))}
                  {methodSteps > 2 && (
                    <p className="text-gray-400 dark:text-gray-500 mt-1">
                      ... and {methodSteps - 2} more steps
                    </p>
                  )}
                </div>
              )}
              
              {expandedSections.method && recipe.method && (
                <div className="p-4 text-sm text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
                  {recipe.method}
                </div>
              )}
            </div>

            {/* Notes Section (if present) */}
            {recipe.notes && recipe.notes.trim() && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleSection('notes')}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">💡</span>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900 dark:text-white">Notes & Tips</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Additional information
                      </p>
                    </div>
                  </div>
                  {expandedSections.notes ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                
                {expandedSections.notes && (
                  <div className="p-4 text-sm text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {recipe.notes}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Button
            onClick={onEdit}
            variant="outline"
            className="flex-1 h-12 text-base border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <Edit3 className="h-4 w-4 mr-2" />
            Edit Details
          </Button>
          <Button
            onClick={handleSaveWithImage}
            disabled={isGeneratingImage}
            className="flex-1 h-12 text-base bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-lg disabled:opacity-50"
          >
            {isGeneratingImage ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Save Recipe
              </>
            )}
          </Button>
        </div>

        {/* Hint */}
        <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-6">
          <Sparkles className="inline h-3 w-3 mr-1" />
          You can always edit your recipe later from the recipe page
        </p>
      </div>
    </div>
  );
};

export default ParsedRecipePreview;
